import React, {useEffect, useState} from "react";

import {
  ChangeDailyItemData,
  PartialOrder,
  PartialOrderItem,
} from "@apps/daily-menu/interfaces";

import {
  EventOrigin,
  recordConversionEvent,
  recordEvent,
} from "@globals/config/eventUtils";

import NomiconIcon from "@components/NomiconIcon";
import TransparentButton from "@components/TransparentButton";
import NumberInput from "@components/forms/inputs/NumberInput";

import SpecialInstructions from "../SpecialInstructions/SpecialInstructions";
import styles from "./DailyCartItem.scss";

type DailyCartItemProps = {
  orderItem: PartialOrderItem;
  orderId: number;
  locationId: number;
  handleItemUpdate: (itemData: ChangeDailyItemData) => Promise<PartialOrder>;
};

export default function DailyCartItem({
  orderItem,
  orderId,
  locationId,
  handleItemUpdate,
}: DailyCartItemProps): JSX.Element {
  const [itemCount, setItemCount] = useState(orderItem.count);

  const handleCountChange = (value: number) => {
    setItemCount(value);
    void handleItemUpdate({
      item: orderItem,
      quantity: value,
      orderId: orderId,
    })
      .then(() => {
        // record events;
        if (value === 0) {
          recordConversionEvent(
            EventOrigin.CART,
            "remove_from_cart",
            orderItem.price,
            {
              items: [
                {
                  item_id: orderItem.id.toString(),
                  item_name: orderItem.name,
                  quantity: orderItem.count,
                },
              ],
            }
          );
        } else {
          recordEvent(EventOrigin.CART, "item quantity change", orderItem.id);
        }
      })
      .catch(() => {
        setItemCount(orderItem.count);
      });
  };

  const addSpecialInstructions = (value: string) => {
    handleItemUpdate({
      item: orderItem,
      quantity: itemCount,
      orderId: orderId,
      specialInstructions: value,
    });
  };

  useEffect(() => setItemCount(orderItem.count), [orderItem]);

  return (
    <div className={styles.dailyCartItem}>
      <div className={styles.orderItemNameContainer}>
        <div className={styles.itemQtyWrapper}>
          <NumberInput
            showButtons={false}
            name={`item-${orderItem.id}-count`}
            value={itemCount}
            onChange={handleCountChange}
          />
        </div>
        <span className={styles.itemName}>{orderItem.name}</span>
        <span className={styles.itemPrice}>${orderItem.price}</span>
        <TransparentButton
          className={styles.itemDelete}
          aria-label={`Remove ${orderItem.name} from cart`}
          onClick={() => {
            handleCountChange(0);
          }}
        >
          <NomiconIcon icon="nomicon-dismiss" />
        </TransparentButton>
      </div>
      <SpecialInstructions
        locationId={locationId}
        specialInstructions={orderItem.special_instructions}
        onDataSubmit={addSpecialInstructions}
      />
      {orderItem.has_side_item && (
        <div className={styles.cartItemSubtext}>
          This meal comes in 2 containers. Be sure to grab both!
        </div>
      )}
    </div>
  );
}
